import { useState } from 'react';
import { useMutation } from 'react-query';
import { api } from '../../../services/api';
import { Panel, PanelHeader, PanelBody } from '../../ui/Panel';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import MoreMenu from '../../ui/MoreMenu';
import Pill from '../../ui/Pill';
import { ErrorNotice, SkeletonRows } from '../../ui/States';
import { errorMessage, inputClass, money } from '../../../lib/programs';
import { EventMoney, Vendor, dateText } from '../../../lib/eventMoney';
import type { SimplePanelProps } from '../adapt';
import { headRow, num, td, th } from '../logistics';
import { useMarkPresent, usePresent } from '../dayOfTypes';
import {
  CountsOnly,
  MoneyField,
  budgetLeadKey,
  cents,
  dollarsIn,
  moneyError,
  useEventMoney,
  useMoneyRefresh,
} from './BudgetPanel';
import { DeadlineList } from './VendorDeadlinesPanel';
import {
  GIFT_CARDS_WHY,
  GiftCardDialog,
  NotStarted,
  StepChecklist,
  giftCardsKey,
  vendorsKey,
} from './GiftCardRequestsPanel';

function VendorDialog({
  eventId,
  data,
  vendor,
  onDone,
}: {
  eventId: string;
  data: EventMoney;
  vendor?: Vendor;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    vendorType: vendor?.vendorType ?? 'hotel',
    name: vendor?.name ?? '',
    paymentMethod: vendor?.paymentMethod ?? 'invoice',
    pcardIncrease: vendor?.pcardIncrease ?? false,
    costCentre: vendor?.costCentre ?? data.finance?.costCentre ?? '',
    quote: dollarsIn(vendor?.quoteCents),
    committed: dollarsIn(vendor?.committedCents),
    actual: dollarsIn(vendor?.actualCents),
    note: vendor?.note ?? '',
  });
  const refresh = useMoneyRefresh(eventId);
  const save = useMutation(
    async () => {
      const body = {
        ...form,
        quoteCents: cents(form.quote, 'Quote'),
        committedCents: cents(form.committed, 'Committed'),
        actualCents: cents(form.actual, 'Final invoice'),
      };
      return vendor
        ? api.patch(`/events/money/vendors/${vendor.id}`, body)
        : api.post(`/events/${eventId}/money/vendors`, body);
    },
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  return (
    <Modal
      title={vendor ? `Edit ${vendor.name}` : 'Add a vendor'}
      why="No contracts, invoices or card numbers here: York finance keeps those. The platform keeps where things stand and the amounts."
      onClose={onDone}
      width={640}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
            {vendor ? 'Save' : 'Add the vendor'}
          </Button>
          <Button onClick={onDone}>Cancel</Button>
        </>
      }
    >
      {save.error ? moneyError(save.error) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MoneyField label="What for" hint={vendor ? 'Set when the vendor was added.' : undefined}>
          <select
            className={inputClass}
            value={form.vendorType}
            onChange={set('vendorType')}
            disabled={Boolean(vendor)}
          >
            {Object.entries(data.vendorTypes!).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </MoneyField>
        <MoneyField label="Vendor">
          <input
            className={inputClass}
            value={form.name}
            onChange={set('name')}
            placeholder="For example, Schulich ELC"
          />
        </MoneyField>
        <MoneyField label="Paid by">
          <select className={inputClass} value={form.paymentMethod} onChange={set('paymentMethod')}>
            {Object.entries(data.paymentMethods!).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </MoneyField>
        <MoneyField label="Cost centre">
          <input className={inputClass} value={form.costCentre} onChange={set('costCentre')} />
        </MoneyField>
        <MoneyField label="Quote">
          <input
            className={inputClass}
            value={form.quote}
            onChange={set('quote')}
            inputMode="decimal"
            placeholder="$"
          />
        </MoneyField>
        <MoneyField label="Committed">
          <input
            className={inputClass}
            value={form.committed}
            onChange={set('committed')}
            inputMode="decimal"
            placeholder="The quote, once approved"
          />
        </MoneyField>
        <MoneyField label="Final invoice">
          <input
            className={inputClass}
            value={form.actual}
            onChange={set('actual')}
            inputMode="decimal"
            placeholder="$"
          />
        </MoneyField>
        {form.paymentMethod === 'pcard' ? (
          <label className="md:col-span-2 inline-flex items-center gap-3 min-h-[44px] text-ink">
            <input
              type="checkbox"
              className="w-[18px] h-[18px] accent-accent"
              checked={form.pcardIncrease}
              onChange={(e) => setForm({ ...form, pcardIncrease: e.target.checked })}
            />
            Needs a temporary PCard limit increase (the NPI approves it)
          </label>
        ) : null}
        <MoneyField label="Note" wide>
          <input className={inputClass} value={form.note} onChange={set('note')} />
        </MoneyField>
      </div>
    </Modal>
  );
}

function ChargeDialog({
  vendor,
  data,
  eventId,
  onDone,
}: {
  vendor: Vendor;
  data: EventMoney;
  eventId: string;
  onDone: () => void;
}) {
  const [form, setForm] = useState({ on: '', kind: 'no_show', rooms: '', nights: '', charge: '', note: '' });
  const refresh = useMoneyRefresh(eventId);
  const save = useMutation(
    async () =>
      api.post(`/events/money/vendors/${vendor.id}/charges`, {
        on: form.on || null,
        kind: form.kind,
        rooms: form.rooms || null,
        nights: form.nights || null,
        chargeCents: cents(form.charge, 'Charge'),
        note: form.note,
      }),
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  return (
    <Modal
      title="Record a no-show or cancellation"
      why={`What ${vendor.name} charged for rooms not used. It adds to the vendor’s charges and the budget.`}
      onClose={onDone}
      width={620}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
            Record it
          </Button>
          <Button onClick={onDone}>Cancel</Button>
        </>
      }
    >
      {save.error ? moneyError(save.error) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MoneyField label="Date">
          <input type="date" className={inputClass} value={form.on} onChange={set('on')} />
        </MoneyField>
        <MoneyField label="What">
          <select className={inputClass} value={form.kind} onChange={set('kind')}>
            {Object.entries(data.chargeKinds!).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </MoneyField>
        <MoneyField label="Rooms">
          <input className={inputClass} value={form.rooms} onChange={set('rooms')} inputMode="numeric" />
        </MoneyField>
        <MoneyField label="Nights">
          <input className={inputClass} value={form.nights} onChange={set('nights')} inputMode="numeric" />
        </MoneyField>
        <MoneyField label="Charge">
          <input
            className={inputClass}
            value={form.charge}
            onChange={set('charge')}
            inputMode="decimal"
            placeholder="$"
          />
        </MoneyField>
        <MoneyField label="Note" hint="Who, and why.">
          <input className={inputClass} value={form.note} onChange={set('note')} />
        </MoneyField>
      </div>
    </Modal>
  );
}

const subhead = 'text-[15px] leading-5 font-semibold text-ink mb-2';

function VendorBlock({
  vendor,
  data,
  eventId,
  canEdit,
}: {
  vendor: Vendor;
  data: EventMoney;
  eventId: string;
  canEdit: boolean;
}) {
  const refresh = useMoneyRefresh(eventId);
  const [editing, setEditing] = useState(false);
  const [charging, setCharging] = useState(false);
  const [beo, setBeo] = useState('');
  const [centre, setCentre] = useState(vendor.costCentre ?? data.finance?.costCentre ?? '');
  const step = useMutation(
    async ({ key, undo }: { key: string; undo: boolean }) =>
      api.post(`/events/money/vendors/${vendor.id}/steps/${key}`, {
        undo,
        ...(key === 'npi_approved' ? { costCentre: centre } : {}),
      }),
    { onSuccess: refresh },
  );
  const addBeo = useMutation(async () => api.post(`/events/money/vendors/${vendor.id}/beo`, { label: beo }), {
    onSuccess: () => {
      setBeo('');
      refresh();
    },
  });
  const cancel = useMutation(
    async () => api.patch(`/events/money/vendors/${vendor.id}`, { cancelled: !vendor.cancelledAt }),
    { onSuccess: refresh },
  );
  const beoApplies = ['hotel', 'venue_catering'].includes(vendor.vendorType);
  const off = Boolean(vendor.cancelledAt);
  const amounts: [string, number | null][] = [
    ['Quote', vendor.quoteCents],
    ['Committed', vendor.committedCents],
    ['Final invoice', vendor.actualCents],
    ['Charges', vendor.chargesCents || null],
  ];
  return (
    <article className="py-8 border-t border-line first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          <h3 className="text-[17px] leading-6 font-semibold text-ink">{vendor.name}</h3>
          <p className="text-small text-ink-3">
            {vendor.typeLabel}
            {data.paymentMethods && vendor.paymentMethod ? ` · ${data.paymentMethods[vendor.paymentMethod]}` : ''}
            {vendor.pcardIncrease ? ' · PCard limit increase' : ''}
            {vendor.costCentre ? ` · cost centre ${vendor.costCentre}` : ''}
          </p>
        </div>
        {off ? (
          <Pill tone="inactive">Cancelled</Pill>
        ) : vendor.complete ? (
          <Pill tone="done">Paid in full</Pill>
        ) : (
          <Pill tone="running">{`Next: ${vendor.next ?? '–'}`}</Pill>
        )}
      </div>
      <div className="grid gap-x-12 gap-y-6 mt-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="min-w-0">
          <StepChecklist
            steps={vendor.steps}
            disabled={!canEdit || step.isLoading || off}
            onToggle={(s) => step.mutate({ key: s.key, undo: s.done })}
            extra={(s) =>
              s.key === 'npi_approved' && !s.done && canEdit && !off ? (
                <input
                  className="flex-none w-[180px] min-h-[36px] px-3 py-[6px] border border-line-strong rounded-sm bg-white text-[15px] text-ink"
                  aria-label="Cost centre for the NPI’s approval"
                  placeholder="Cost centre"
                  value={centre}
                  onChange={(e) => setCentre(e.target.value)}
                />
              ) : null
            }
          />
          {step.error ? (
            <div className="mt-2">
              <ErrorNotice>{errorMessage(step.error, 'Could not record the step.')}</ErrorNotice>
            </div>
          ) : null}
        </div>
        <div className="min-w-0">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
            {amounts.map(([label, value]) => (
              <div key={label}>
                <dt className="text-small text-ink-3">{label}</dt>
                <dd className="text-[15px] leading-[22px] text-ink tabular">
                  {value != null ? money(value) : <span className="text-ink-3">–</span>}
                </dd>
              </div>
            ))}
          </dl>
          {beoApplies ? (
            <div className="mt-5">
              <p className={subhead}>Banquet event orders</p>
              <p className="text-[15px] leading-[22px] text-ink-2">
                {vendor.beoVersions.length
                  ? vendor.beoVersions.map((b) => `${b.label} (${dateText(b.date)})`).join(' → ')
                  : 'None yet.'}
              </p>
              {canEdit && !off ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  <input
                    className="w-[160px] min-h-[44px] px-3 py-[10px] border border-line-strong rounded-sm bg-white text-table text-ink"
                    aria-label="New banquet event order version"
                    placeholder="BEO 2.0"
                    value={beo}
                    onChange={(e) => setBeo(e.target.value)}
                  />
                  <Button disabled={!beo || addBeo.isLoading} onClick={() => addBeo.mutate()}>
                    Add version
                  </Button>
                </div>
              ) : null}
              {addBeo.error ? <ErrorNotice>{errorMessage(addBeo.error, 'Could not add it.')}</ErrorNotice> : null}
            </div>
          ) : null}
        </div>
      </div>
      {vendor.deadlines.length ? (
        <div className="mt-6">
          <p className={subhead}>Deadlines</p>
          <DeadlineList
            eventId={eventId}
            canEdit={canEdit}
            deadlines={vendor.deadlines.map((dl) => ({ ...dl, vendorId: vendor.id }))}
          />
        </div>
      ) : null}
      {vendor.vendorType === 'hotel' ? (
        <div className="mt-6">
          <p className={subhead}>No-shows and cancellations</p>
          {vendor.charges.length ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className={headRow}>
                    <th className={th}>Date</th>
                    <th className={th}>What</th>
                    <th className={`${th} ${num}`}>Rooms</th>
                    <th className={`${th} ${num}`}>Nights</th>
                    <th className={`${th} ${num}`}>Charge</th>
                    <th className={th}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {vendor.charges.map((c) => (
                    <tr key={c.id} className="border-b border-line">
                      <td className={`${td} whitespace-nowrap`}>{dateText(c.on)}</td>
                      <td className={td}>{data.chargeKinds?.[c.kind] ?? c.kind}</td>
                      <td className={`${td} ${num}`}>{c.rooms ?? <span className="text-ink-3">–</span>}</td>
                      <td className={`${td} ${num}`}>{c.nights ?? <span className="text-ink-3">–</span>}</td>
                      <td className={`${td} ${num}`}>{money(c.chargeCents)}</td>
                      <td className={`${td} text-ink-2`}>{c.note || <span className="text-ink-3">–</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[15px] leading-[22px] text-ink-2">None recorded.</p>
          )}
          {canEdit ? (
            <Button variant="quiet" size="sm" className="px-0 mt-1" onClick={() => setCharging(true)}>
              Record a no-show or cancellation
            </Button>
          ) : null}
        </div>
      ) : null}
      {vendor.note ? <p className="text-[15px] leading-[22px] text-ink-2 mt-4">{vendor.note}</p> : null}
      {canEdit ? (
        <div className="flex gap-2 mt-4">
          <Button size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
          <MoreMenu
            label={`More for ${vendor.name}`}
            items={[
              {
                label: off ? 'Restore this vendor' : 'Cancel this vendor',
                destructive: !off,
                disabled: cancel.isLoading,
                onClick: () => cancel.mutate(),
              },
            ]}
          />
        </div>
      ) : null}
      {cancel.error ? <ErrorNotice>{errorMessage(cancel.error, 'Could not do that.')}</ErrorNotice> : null}
      {editing ? <VendorDialog eventId={eventId} data={data} vendor={vendor} onDone={() => setEditing(false)} /> : null}
      {charging ? (
        <ChargeDialog vendor={vendor} data={data} eventId={eventId} onDone={() => setCharging(false)} />
      ) : null}
    </article>
  );
}

export const VENDORS_WHY =
  'Add the hotel and the venue, so their deadlines (rooming list, room release, menu, meal guarantee) appear on Timeline. Tick each step as it happens.';

/** Vendor commitments: quote → NPI approval → contract → payment → BEO → final invoice. */
export default function VendorsPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useEventMoney(eventId);
  useMarkPresent(vendorsKey(eventId));
  const giftCardsHere = usePresent(giftCardsKey(eventId));
  const leadHere = usePresent(budgetLeadKey(eventId));
  const [adding, setAdding] = useState<'vendor' | 'gift' | null>(null);
  if (q.isLoading) return <SkeletonRows rows={4} />;
  if (q.error || !q.data) return <ErrorNotice>{errorMessage(q.error, 'Could not load the vendors.')}</ErrorNotice>;
  const d = q.data;
  if (!d.visible)
    return leadHere ? null : (
      <Panel>
        <PanelHeader title="Vendors" />
        <PanelBody>
          <CountsOnly data={d} />
        </PanelBody>
      </Panel>
    );
  const vendors = d.vendors ?? [];
  const dialogs = (
    <>
      {adding === 'vendor' ? <VendorDialog eventId={eventId} data={d} onDone={() => setAdding(null)} /> : null}
      {adding === 'gift' ? <GiftCardDialog eventId={eventId} data={d} onDone={() => setAdding(null)} /> : null}
    </>
  );
  if (!vendors.length)
    return (
      <>
        <NotStarted
          rows={[
            {
              what: 'Vendors',
              why: VENDORS_WHY,
              action: canEdit ? 'Add a vendor' : null,
              onClick: () => setAdding('vendor'),
            },
            ...(giftCardsHere && !(d.giftCardRequests ?? []).length
              ? [
                  {
                    what: 'Gift cards',
                    why: GIFT_CARDS_WHY,
                    action: canEdit ? 'Request gift cards' : null,
                    onClick: () => setAdding('gift'),
                  },
                ]
              : []),
          ]}
        />
        {dialogs}
      </>
    );
  return (
    <Panel>
      <PanelHeader title="Vendors" note={`${d.counts.vendors} · ${d.counts.vendorsComplete} paid in full`}>
        {canEdit ? <Button onClick={() => setAdding('vendor')}>Add a vendor</Button> : null}
      </PanelHeader>
      <PanelBody>
        {vendors.map((v) => (
          <VendorBlock key={v.id} vendor={v} data={d} eventId={eventId} canEdit={canEdit} />
        ))}
      </PanelBody>
      {dialogs}
    </Panel>
  );
}
